import { TEST_GRADES } from "../types";
import type { Child, Grade, Intake } from "../types";

export interface NoticeInput {
  child: Child;
  intake: Intake;
  /** Epoch milliseconds. Printed as the letter's date. */
  today: number;
}

export interface NextStep {
  title: string;
  detail: string;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** "2016-04-11" becomes "April 11, 2016". Text that is not an ISO date is returned unchanged. */
export function longDate(iso: string): string {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [year, month, day] = parts.map(Number);
  const monthName = month ? MONTHS[month - 1] : undefined;
  if (!monthName || !day) return iso;
  return `${monthName} ${day}, ${year}`;
}

/** Long date in the local time zone of the device, which is where the family is. */
export function longDateFromTs(ts: number): string {
  const d = new Date(ts);
  const monthName = MONTHS[d.getMonth()];
  if (!monthName) return "";
  return `${monthName} ${d.getDate()}, ${d.getFullYear()}`;
}

export function isTestGrade(grade: Grade | ""): boolean {
  return grade !== "" && TEST_GRADES.includes(grade);
}

const GRADE_10_FLAG =
  "Grade 10 is worth asking about. HIDOE’s own February 2026 FAQ notes that public school students are no longer tested in grade 10 and are tested in grade 11 instead, while Chapter 12 has not been amended and still asks homeschoolers for a grade 10 score. This is unresolved. Ask your principal in writing which year they expect, and keep the reply with your records.";

export function grade10Flag(grade: Grade | ""): string | null {
  return grade === "Grade 10" ? GRADE_10_FLAG : null;
}

function isEnrolledInHawaiiPublicSchool(intake: Intake): boolean {
  return intake.enrollment === "hi-public";
}

interface NoticeFields {
  child: string;
  birthdate: string;
  grade: string;
  address: string;
  phone: string;
  parent: string;
  school: string;
  principal: string;
  startDate: string;
}

function readFields({ child, intake }: NoticeInput): NoticeFields {
  return {
    child: child.legalName.trim() || child.firstName.trim(),
    birthdate: longDate(child.birthdate.trim()),
    grade: child.grade,
    address: intake.address.trim(),
    phone: intake.parentPhone.trim(),
    parent: intake.parentName.trim(),
    school: intake.schoolOfRecord.trim(),
    principal: intake.principal.trim(),
    startDate: longDate(intake.startDate.trim()),
  };
}

export function buildLetter(input: NoticeInput): string {
  const f = readFields(input);
  const child = f.child || "[child’s full name]";
  const address = f.address || "[street address, city, HI, zip]";
  const phone = f.phone || "[telephone number]";
  const school = f.school || "[school of record]";

  const enrolledParagraph = isEnrolledInHawaiiPublicSchool(input.intake)
    ? [
        `My child is currently enrolled at ${school}. I have also`,
        "submitted DOE Form 4140 requesting withdrawal under Section B,",
        "item 5, homeschooling.",
        "",
      ]
    : [];

  return [
    longDateFromTs(input.today),
    "",
    f.principal || "Principal",
    school,
    "",
    "Re:  Notice of intent to homeschool, HAR §8-12-13",
    "",
    f.principal ? `Dear Principal ${f.principal}:` : "Dear Principal:",
    "",
    "This letter is my notice of intent to provide a home school program",
    "for my child, as provided by Hawai‘i Administrative Rules Title 8,",
    "Chapter 12. The required information follows.",
    "",
    `    Child’s name        ${child}`,
    `    Date of birth       ${f.birthdate || "[date of birth]"}`,
    `    Grade level         ${f.grade || "[grade level]"}`,
    `    Address             ${address}`,
    `    Telephone           ${phone}`,
    "",
    `The home school program begins ${f.startDate || "[start date]"}.`,
    "",
    ...enrolledParagraph,
    "I will keep a record of the planned curriculum as described in",
    "§8-12-15, and I will submit an annual report of my child’s",
    "progress as described in §8-12-18.",
    "",
    "Please acknowledge receipt of this notice in writing.",
    "",
    "Respectfully,",
    "",
    "",
    "_________________________________",
    `${f.parent || "[your name]"}, parent`,
    address,
    phone,
  ].join("\n");
}

const FORM_4140_STEP: NextStep = {
  title: "Fill out DOE Form 4140 as well",
  detail:
    "Section A is the withdrawal request, signed and dated by the parent or guardian. In Section B, check item 5, homeschooling, and enter your start date. Homeschooling is the one option on that form that needs an acknowledgment rather than an approval.",
};

function deliverStep(school: string): NextStep {
  return {
    title: `Deliver the notice to the principal at ${school}`,
    detail:
      "Hand delivery with a date stamp on your copy works. If you mail it, use certified mail with return receipt, which is what the Hawai‘i homeschool groups have recommended for years. You want proof of the date.",
  };
}

const EVERY_FAMILY_STEPS: readonly NextStep[] = [
  {
    title: "Start teaching",
    detail:
      "There is no approval to wait for. Your program begins on the date in the letter. The school acknowledges receipt; it does not grant permission.",
  },
  {
    title: "Keep the acknowledgment",
    detail:
      "File the school’s written acknowledgment with your records. It is the document that answers any later question about when you started.",
  },
  {
    title: "Begin the record on day one",
    detail:
      "Not a plan for the year. A record of what happened. Dates, hours, subjects, how you knew it landed, and what you used. The annual progress report is assembled out of that record, so a record kept from day one costs you nothing in June.",
  },
];

function testingStep(grade: Grade): NextStep {
  return {
    title: "Plan for standardized testing this year",
    detail: `${grade} is one of the four grades where §8-12-18 asks for a nationally normed standardized test score. You may test through the school’s statewide testing program, or privately at your own expense. Ask the principal now rather than in spring.`,
  };
}

export function buildNextSteps(input: NoticeInput): NextStep[] {
  const school = input.intake.schoolOfRecord.trim() || "your school of record";
  const grade = input.child.grade;
  return [
    ...(isEnrolledInHawaiiPublicSchool(input.intake) ? [{ ...FORM_4140_STEP }] : []),
    deliverStep(school),
    ...EVERY_FAMILY_STEPS.map((step) => ({ ...step })),
    ...(grade !== "" && isTestGrade(grade) ? [testingStep(grade)] : []),
  ];
}

export function missingForNotice({ child, intake }: NoticeInput): string[] {
  const required: ReadonlyArray<readonly [name: string, value: string]> = [
    ["child's full name", child.legalName],
    ["date of birth", child.birthdate],
    ["grade level", child.grade],
    ["home address", intake.address],
    ["telephone", intake.parentPhone],
    ["parent name", intake.parentName],
    ["school of record", intake.schoolOfRecord],
    ["start date", intake.startDate],
  ];
  return required.filter(([, value]) => value.trim() === "").map(([name]) => name);
}
